import {utils} from '@wallaby-ui/core';
import {
  cloneDeep,
  filter,
  find,
  forEach,
  forOwn,
  groupBy,
  includes,
  isEmpty,
  map,
  uniq,
} from 'lodash';
import {normalize, schema} from 'normalizr';
import {
  PROPERTY_TYPE_CASE,
  PROPERTY_TYPE_CASE_PROJECT,
} from 'src/js/model/column_config';
import {SearchTypeViews} from 'src/js/model/search_type_views';

const FILTER_CHILDREN = 'children';
const FILTER_CHILDREN_DB_KEY = 'childrenDbKey';
const FILTER_HIERARCHY_TYPE = 'hierarchyType';
const FILTER_AUTOCOMPLETE_METHOD = 'autocompleteMethod';
const FILTER_HIDE_IF_EMPTY_OR_SINGLE_VALUE = 'hideIfEmptyOrSingleValue';
const FILTER_PROPERTY_TYPE = 'propertyType';
const FILTER_UI = 'ui';
const FILTER_LABEL = 'label';
const FILTER_PLACEHOLDER = 'placeholder';
const FILTER_NAME = 'name';
const FILTER_TYPE = 'type';
const FILTER_EXPANDED = 'expanded';
const FILTER_INDENT = 'indent';
const FILTER_MIN = 'min';
const FILTER_MAX = 'max';
const FILTER_RANGE_PROPERTIES = 'rangeProperties';
const FILTER_STEP = 'step';
const FILTER_NOT_REMOVABLE = 'notRemovable';
const FILTER_SEARCH_PREFIX = 'searchPrefix';

export const FILTER_DEFAULT_VALUE = 'defaultValue';
const FILTER_DEFAULT_VALUE_DB_KEY = 'defaultValueDbKey';
const FILTER_FORMATTER_PARAMS = 'formatterParams';
const FILTER_SECURED = 'secured';

const FILTER_GROUP_LABEL_CASE = 'filter.group.case';
const FILTER_GROUP_LABEL_DOCUMENT = 'filter.group.document';
const FILTER_GROUP_TYPE_CASE = 'CASE';
const FILTER_GROUP_TYPE_DOCUMENT = 'DOCUMENT';

export const FILTER_SCHEMA_ENTITIES = 'entities';
export const FILTER_SCHEMA_RESULT = 'result';
const FILTER_SCHEMA_UID = 'uid';
export const FILTER_SCHEMA_ENUM_NAME = 'enumName';
export const FILTER_SCHEMA_NUMBER_RANGE_NAME = 'numberRangeFilterName';

export const CONFIGURATION_CONFIG = 'config';
export const CONFIGURATION_ENTITIES_FILTERS = 'filters';
export const CONFIGURATION_VIEWS = 'views';

export const AutocompleteMethod = {
  BY_PATH: 'byPath',
  BY_LABEL: 'byLabel',
};

export class FilterNode {
  constructor(rawData = {}, id = undefined) {
    this.rawData = {...rawData};
    this.id = id;
  }

  clone() {
    return Object.assign(
      new FilterNode(cloneDeep(this.rawData), this.id),
      this
    );
  }

  getId() {
    return this.id;
  }

  getLabel() {
    return (
      (this.rawData[FILTER_UI]
        ? this.rawData[FILTER_UI][FILTER_LABEL]
        : this.rawData[FILTER_LABEL]) ?? this.getName()
    );
  }

  getPlaceholder() {
    return this.rawData[FILTER_UI]?.[FILTER_PLACEHOLDER];
  }

  getName() {
    return this.rawData[FILTER_NAME];
  }

  getRawData() {
    return this.rawData;
  }

  getType() {
    return this.rawData[FILTER_UI]
      ? this.rawData[FILTER_UI][FILTER_TYPE]
      : undefined;
  }

  isExpandedByDefault() {
    return this.rawData[FILTER_UI]
      ? this.rawData[FILTER_UI][FILTER_EXPANDED]
      : false;
  }

  getIndent() {
    return this.rawData[FILTER_UI]
      ? this.rawData[FILTER_UI][FILTER_INDENT]
      : undefined;
  }

  isNotRemovable() {
    return this.rawData[FILTER_NOT_REMOVABLE];
  }

  isSecured() {
    return this.rawData[FILTER_SECURED];
  }

  getDefaultValue() {
    return this.rawData[FILTER_DEFAULT_VALUE] || null;
  }

  getDefaultValueDbKey() {
    return this.rawData[FILTER_DEFAULT_VALUE_DB_KEY];
  }

  getFormatterParams() {
    return this.rawData[FILTER_FORMATTER_PARAMS] || {};
  }

  hasDefaultValue() {
    return Boolean(this.getDefaultValue() || this.getDefaultValueDbKey());
  }

  getChildrenDbKey() {
    return this.rawData[FILTER_CHILDREN_DB_KEY];
  }

  getHideIfEmptyOrSingleValue() {
    return this.rawData[FILTER_HIDE_IF_EMPTY_OR_SINGLE_VALUE];
  }

  getChildrenIds() {
    return this.rawData[FILTER_CHILDREN] || [];
  }

  getChildLabel(filterSchema, name) {
    let resultLabel = name;
    for (
      let childIdx = 0, len = this.getChildrenIds().length;
      childIdx < len;
      childIdx++
    ) {
      const childNode = filterSchema.getFilter(this.getChildrenIds()[childIdx]);
      if (childNode && childNode.getName() === name) {
        if (childNode.getLabel()) {
          resultLabel = childNode.getLabel();
        }
        break;
      }
    }
    return resultLabel;
  }

  getHierarchyType() {
    return this.rawData[FILTER_HIERARCHY_TYPE];
  }

  getAutocompleteMethod() {
    return (
      this.rawData[FILTER_AUTOCOMPLETE_METHOD] || AutocompleteMethod.BY_LABEL
    );
  }

  getMin() {
    return this.rawData[FILTER_UI]
      ? this.rawData[FILTER_UI][FILTER_MIN]
      : undefined;
  }

  getMax() {
    return this.rawData[FILTER_UI]
      ? this.rawData[FILTER_UI][FILTER_MAX]
      : undefined;
  }

  getRangeProperties() {
    return this.rawData[FILTER_RANGE_PROPERTIES];
  }

  getPropertyType() {
    return this.rawData[FILTER_PROPERTY_TYPE];
  }

  getSearchPrefix() {
    return this.rawData[FILTER_UI]
      ? this.rawData[FILTER_UI][FILTER_SEARCH_PREFIX]
      : undefined;
  }

  getStep() {
    return this.rawData[FILTER_UI]
      ? this.rawData[FILTER_UI][FILTER_STEP]
      : undefined;
  }

  getEnumName() {
    return this.rawData[FILTER_SCHEMA_ENUM_NAME];
  }

  getNumberRangeFilterName() {
    return this.rawData[FILTER_SCHEMA_NUMBER_RANGE_NAME];
  }
}

export class FilterGroup {
  static determineType(filterObj) {
    return filterObj[FILTER_PROPERTY_TYPE] === PROPERTY_TYPE_CASE ||
      filterObj[FILTER_PROPERTY_TYPE] === PROPERTY_TYPE_CASE_PROJECT
      ? FILTER_GROUP_TYPE_CASE
      : FILTER_GROUP_TYPE_DOCUMENT;
  }

  static determineLabel(groupType) {
    return groupType === FILTER_GROUP_TYPE_CASE
      ? FILTER_GROUP_LABEL_CASE
      : FILTER_GROUP_LABEL_DOCUMENT;
  }

  constructor(
    filters = [],
    views = {},
    type = FILTER_GROUP_TYPE_DOCUMENT,
    id = undefined
  ) {
    this.filters = [...filters];
    this.views = views;
    this.type = type;
    this.id = id;
    this.label = FilterGroup.determineLabel(type);
  }

  getId() {
    return this.id;
  }

  getFilters() {
    return this.filters;
  }

  getViews() {
    return this.views;
  }

  /* eslint-disable no-use-before-define */
  getFiltersByView(viewId = 'document') {
    return FilterSchema.filterFiltersByView(
      this.getFilters(),
      this.getViews(),
      viewId,
      true
    );
  }

  /* eslint-enable no-use-before-define */

  getLabel() {
    return this.label;
  }

  getType() {
    return this.type;
  }
}

export class FilterSchema {
  static initFilters(rawFilters) {
    const filters = [];
    forOwn(rawFilters, (filterObj, id) => {
      filters.push(new FilterNode(filterObj, id));
    });
    return filters;
  }

  static initResultGroups(filterIds, rawFilters, views) {
    let filterGroups = [];
    if (rawFilters && filterIds) {
      const filtersByGroup = map(filterIds, (filterId) => ({
        id: filterId,
        type: FilterGroup.determineType(rawFilters[filterId]),
        [FILTER_NAME]: rawFilters[filterId][FILTER_NAME],
      }));
      const groupedFilters = groupBy(filtersByGroup, 'type');

      filterGroups = map(
        groupedFilters,
        (group, type) =>
          new FilterGroup(group, views, type, utils.generateGUID())
      );
    }
    return filterGroups;
  }

  static generateIds(filters) {
    const filtersWithIds = [];
    if (filters && filters.length > 0) {
      filters.forEach((filterItem) => {
        const filterWithId = {...filterItem};
        if (
          !Object.prototype.hasOwnProperty.call(filterItem, FILTER_SCHEMA_UID)
        ) {
          filterWithId[FILTER_SCHEMA_UID] = utils.generateGUID();
        }
        if (filterItem[FILTER_CHILDREN]) {
          filterWithId[FILTER_CHILDREN] = FilterSchema.generateIds(
            filterItem[FILTER_CHILDREN]
          );
        }
        filtersWithIds.push(filterWithId);
      });
    }
    return filtersWithIds;
  }

  static normalizeSchema(rawData) {
    let normalizedResult = rawData;
    if (!isEmpty(rawData)) {
      // generate ids
      normalizedResult[CONFIGURATION_CONFIG][CONFIGURATION_ENTITIES_FILTERS] =
        FilterSchema.generateIds(
          normalizedResult[CONFIGURATION_CONFIG][CONFIGURATION_ENTITIES_FILTERS]
        );

      const filters = new schema.Entity(
        CONFIGURATION_ENTITIES_FILTERS,
        {},
        {idAttribute: FILTER_SCHEMA_UID}
      );
      filters.define({
        [FILTER_CHILDREN]: new schema.Array(filters),
      });

      normalizedResult = normalize(normalizedResult[CONFIGURATION_CONFIG], {
        [CONFIGURATION_ENTITIES_FILTERS]: new schema.Array(filters),
      });
    }
    return normalizedResult;
  }

  static filterFiltersByView(
    filters,
    views,
    viewId = 'document',
    areRawFilters = false
  ) {
    let filtersByView = filters;
    if (
      !isEmpty(views) &&
      !isEmpty(views.getView(viewId)) &&
      views.getView(viewId).getItems()
    ) {
      // get filters only from the wanted view
      const view = views.getView(viewId).getItems();
      filtersByView = filter(filters, (filterObj) =>
        includes(
          view,
          areRawFilters ? filterObj[FILTER_NAME] : filterObj.getName()
        )
      );
    }
    return filtersByView;
  }

  static collectDataSetIdsFromFilters(filters) {
    const result = [];
    if (filters) {
      forEach(filters, (filterItem) => {
        if (filterItem.getChildrenDbKey()) {
          result.push(filterItem.getChildrenDbKey());
        }
      });
    }
    return result;
  }

  static collectDataSetIds(filters) {
    return uniq(FilterSchema.collectDataSetIdsFromFilters(filters));
  }

  constructor(rawData = {}) {
    this.rawData = {...rawData};
    this.filters = [];
    this.dataSetIds = [];
    this.loaded = false;
    if (this.rawData) {
      if (this.rawData[CONFIGURATION_CONFIG]) {
        this.views = new SearchTypeViews(
          this.rawData[CONFIGURATION_CONFIG][CONFIGURATION_VIEWS]
        );
      }
      // normalize schema and set it to this.rawData
      this.rawData = FilterSchema.normalizeSchema(this.rawData);
      if (
        this.rawData &&
        this.rawData[FILTER_SCHEMA_ENTITIES] &&
        this.rawData[FILTER_SCHEMA_ENTITIES][CONFIGURATION_ENTITIES_FILTERS]
      ) {
        this.filters = FilterSchema.initFilters(
          this.rawData[FILTER_SCHEMA_ENTITIES][CONFIGURATION_ENTITIES_FILTERS]
        );
        this.resultGroups = FilterSchema.initResultGroups(
          this.rawData[FILTER_SCHEMA_RESULT][CONFIGURATION_ENTITIES_FILTERS],
          this.rawData[FILTER_SCHEMA_ENTITIES][CONFIGURATION_ENTITIES_FILTERS],
          this.views
        );
        this.dataSetIds = FilterSchema.collectDataSetIds(this.filters);
        this.loaded = true;
      }
    }
  }

  clone() {
    return Object.assign(new FilterSchema(cloneDeep(this.rawData)), this);
  }

  isLoaded() {
    return this.loaded;
  }

  getDefaultValueFilters(viewId = 'document') {
    const filters = this.getFiltersByView(viewId);
    return filter(filters, (filterObj) => filterObj.hasDefaultValue());
  }

  hasDefaultValueFilters(viewId = 'document') {
    const defaultFilters = this.getDefaultValueFilters(viewId);
    return defaultFilters.length > 0;
  }

  getFilters() {
    return this.filters;
  }

  getFiltersByView(viewId = 'document') {
    return FilterSchema.filterFiltersByView(
      this.getFilters(),
      this.getViews(),
      viewId
    );
  }

  getFilter(id) {
    return find(this.filters, (filterObj) => filterObj.getId() === id);
  }

  getFilterByName(name) {
    return find(this.filters, (filterObj) => filterObj.getName() === name);
  }

  hasNotRemovableFilters() {
    return Boolean(
      find(this.filters, (filterObj) => filterObj.isNotRemovable())
    );
  }

  getResult() {
    let result = [];
    if (
      this.rawData &&
      this.rawData[FILTER_SCHEMA_ENTITIES] &&
      this.rawData[FILTER_SCHEMA_RESULT][CONFIGURATION_ENTITIES_FILTERS]
    ) {
      result =
        this.rawData[FILTER_SCHEMA_RESULT][CONFIGURATION_ENTITIES_FILTERS];
    }
    return result;
  }

  getResultGroups() {
    return this.resultGroups;
  }

  getViews() {
    return this.views;
  }

  getDataSetIds() {
    return this.dataSetIds;
  }
}

export class FiltersConfig {
  constructor(rawData = {}) {
    this.rawData = {...rawData};
    this.loaded = !isEmpty(this.rawData) || false;
  }

  isLoaded() {
    return this.loaded;
  }
}

export default {FiltersConfig, FilterNode, FilterSchema};
